import { ReactNode } from 'react';
import { createBrowserRouter } from 'react-router-dom';
import { AdminToolsMain } from '@modules/admin/AdminTools/Main/Main';
import { Users } from '@modules/admin/AdminTools/Users/Users';
import { TeamManager } from '@modules/admin/AdminTools/TeamManager';
import { Roles } from '@modules/admin/AdminTools/Roles';
import { ConfigurationTable } from '@modules/admin/AdminTools/ConfigurationTable/ConfigurationTable';
import { LenderUtility } from '@modules/admin/Tools/LenderUtility/LenderUtility';
import { LenderRank } from '@modules/admin/Tools/LenderRank/LenderRank';
import { Leads } from '@modules/admin/LCMenu/MyLCQueues/Leads/Leads';
import { Deals } from '@modules/admin/LCMenu/MyLCQueues/Deals/Deals';
import { LenderEditor } from '@modules/admin/Tools/LenderEditor/LenderEditor';
import { StipTypeEditor } from '@modules/admin/AdminTools/StipTypeEditor/StipTypeEditor';
import { ProductRules } from '@modules/admin/Tools/ProductRules/ProductRules';
import { ReadytoShip } from '@modules/fedex/ReadytoShip/ReadytoShip';
import { ActiveShipment } from '@modules/fedex/ActiveShipment/ActiveShipment';
import { FundedShipment } from '@modules/fedex/FundedShipment/FundedShipment';
import { Workflows } from '@modules/admin/AdminTools/Workflows/Workflows';
import { DripFeederWrapper } from '@modules/admin/AdminTools/DripFeeder/DripFeederWrapper';
import QueueConfiguration from '@modules/admin/AdminTools/QueueConfiguration/QueueConfiguration';
import { Addresses } from '@modules/fedex/Addresses/Addresses';
import { CustomerCommunications } from '@modules/admin/Tools/CustomerCommunications/CustomerCommunications';
import { Reminders } from '@modules/admin/LCMenu/MyLCQueues/Reminders/Reminders';
import { DealStatusReminders } from '@modules/admin/LCMenu/MyLCQueues/DealStatusReminders/DealStatusReminders';
import { StateRequirements } from '@modules/titles/TitlesAdmin/StateRequirements/StateRequirements';
import { LeadSourceConfiguration } from '@modules/admin/AdminTools/LeadSourceConfiguration/LeadSourceConfiguration';
import { TitlesDVMPacket } from '@modules/titles/TitlesAdmin/TitlesDVMPacket/TitlesDVMPacket';
import { LenderELTEditor } from '@modules/titles/TitlesAdmin/LenderELTEditor/LenderELTEditor';
import { LienHolderAddresses } from '@modules/admin/Tools/LienHolderAddresses';
import { OneClickEdit } from '@modules/OneClickEdit';
import TitleScanConfig from '@modules/admin/Tools/TitleScanConfig';
import { LoanRestrictionsConfiguration } from '@modules/admin/AdminTools/LoanRestrictionsConfiguration';
import { DealSheetsTable } from '@modules/admin/AdminTools/DealSheets/DealSheetsTable';
import StateUsers from '@modules/admin/AdminTools/StateUsers/StateUsers';
import { ImportProductCancel } from '@modules/accounting/CheckerMenu/DealsQueue/ImportProductCancel';
import StateLenderRestrictions from '@modules/admin/AdminTools/StateLenderRestrictions';
import { CheckRequestConfigureSubGroup, ECheckRequest } from '@modules/accounting/CheckerMenu/interfaces';
import { PhoenixDeals } from '@modules/accounting/CheckerMenu/DealsQueue/PhoenixDeals';
import { UnIdentifiedDeals } from '@modules/accounting/CheckerMenu/DealsQueue/UnIdentifiedDeals';
import { Tracker } from '@modules/admin/Tracker/Tracker';
import { Goals } from '@modules/admin/Goals';
import { isExternalLink } from '@shared/components/Layout/components/Sidebar/utils';
import { MeridianLenderConfiguration } from '@modules/admin/AdminTools/MeridianLenderConfiguration/MeridianLenderConfiguration';
import { RoutePlaceholder } from '@shared/components/RoutePlaceholder';
import OutOfSyncDS from '@modules/admin/AdminTools/OutOfSyncDS/OutOfSyncDS';
import PayPlanConfig from '@modules/admin/AdminTools/PayPlan';
import { ChangeWorkflowStep } from '@modules/admin/Tools/ChangeWorkflowStep';
import { ChangeFundedDate } from '@modules/admin/Tools/ChangeFundedDate/ChangeFundedDate';
import { MenuEditor } from '@modules/admin/AdminTools/MenuEditor';
import { ContactManager } from '@modules/ContactManager';
import { StipsReviewTab } from '@modules/ContactManager/components/Tabs/components/ContactFiles/components/StipsReviewTab';
import { LenderInfo } from '@modules/ContactManager/components/Tabs/components/Deals/components/LenderInfo/LenderInfo';
import { CreateContact } from '@modules/admin/AdminTools/CreateContact';
import { RegistrationInfo } from '@modules/registration/RegistrationInfo';
import { DealSheet } from '@modules/shared/DealSheet/DealSheet';
import { SearchResult } from '@modules/shared/SearchResult/SearchResult';
import { Layout } from '@shared/components/Layout';
import { UserWithoutRoles } from '@shared/components/UserWithoutRoles';
import { IMenuItem } from '@shared/components/Layout/components/Sidebar/types';
import { BASE_NAME } from '@shared/constants/constants';
import { Dialer } from '@modules/dialer/Dialer';
import { DefaultPage } from '@shared/components/DefaultPage';
import { CheckRequest } from '@modules/accounting/CheckerMenu/CheckRequest';
import { withQueryParams } from '@shared/commonFunctions';
import { TitleFees } from '@modules/titles/TitlesAdmin/TitleFees/TitleFees';
import { StipsReviewScreen } from '@modules/ContactManager/components/Tabs/components/ContactFiles/components/StipsReviewScreen';

interface ProductCheckConfig  {
   text: string;
   url: string;
   queueId?: string;
   queueType?: string;
}

export interface RouteItem {
  element: ReactNode | null;
  text: string;
  link: string;
}

export interface AppRoutesConfig {
  [index: string]: unknown;
}

export interface urlByKey {
  url: string;
  text: string;
  searchParams: string | null;
}

export interface urlByKeyConfig {
  [index: string]: urlByKey;
}

interface IMenuElementGeneric {
  id: string;
  text: string;
  menuItemType: string;
  link: string;
  alertCount: number;
  showAlert: boolean;
  highlightColor1: string;
  highlightColor2: string;
  order: number;
  queueId: string;
  queueType: string;
  queueCount: number;
  elementKey: string;
}

interface reactRoutes {
  path: string;
  element: string | number | true | React.ReactFragment | JSX.Element;
}

export interface ChildMenuItem extends IMenuElementGeneric {
  element?: ReactNode;
}

export interface ILeftMenuItem extends IMenuElementGeneric {
  childMenuItems: ChildMenuItem[];
}

export interface IElementKeyRoutes {
  elementKey: string;
  url: string;
}

export const appRoutes: AppRoutesConfig = {
  AdminToolsMain: <AdminToolsMain />,
  Users: <Users />,
  TeamManager: <TeamManager />,
  Roles: <Roles />,
  Workflows: <Workflows />,
  DripFeederWrapper: <DripFeederWrapper />,
  DealSheetsTable: <DealSheetsTable />,
  ConfigurationTable: <ConfigurationTable />,
  QueueConfiguration: <QueueConfiguration />,
  LeadSourceConfiguration: <LeadSourceConfiguration />,
  AdminStateUsers: <StateUsers />,
  StateUsers: <StateUsers />,
  LoanRestrictionsConfiguration: <LoanRestrictionsConfiguration />,
  MeridianLenderConfiguration: <MeridianLenderConfiguration />,
  StateLenderRestrictions: <StateLenderRestrictions />,
  StipTypeEditor: <StipTypeEditor />,
  MenuEditor: <MenuEditor />,
  OutOfSyncDS: <OutOfSyncDS />,
  PayPlanConfig: <PayPlanConfig />,
  LenderEditor: <LenderEditor />,
  LenderUtility: <LenderUtility />,
  ProductRules: <ProductRules />,
  ChangeWorkflowStep: <ChangeWorkflowStep />,
  ChangeFundedDate: <ChangeFundedDate />,
  LienHolderAddresses: <LienHolderAddresses />,
  LenderRank: <LenderRank />,
  TitleScanConfig: <TitleScanConfig />,
  CustomerCommunications: <CustomerCommunications />,
  Reminders: <Reminders />,
  DealStatusReminder: <DealStatusReminders />,
  StateRequirements: <StateRequirements />,
  TitlesDVMPacket: <TitlesDVMPacket />,
  LenderELTEditor: <LenderELTEditor />,
  ImportProductCancel: <ImportProductCancel />,
  PhoenixDeals: <PhoenixDeals />,
  UnIdentifiedDeals: <UnIdentifiedDeals />,
  CheckRequest: <CheckRequest configurationSubGroup={CheckRequestConfigureSubGroup.CheckRequest} checkRequestType={ECheckRequest.Check} />,
  CheckCut: <CheckRequest configurationSubGroup={CheckRequestConfigureSubGroup.CheckCut} checkRequestType={ECheckRequest.Check} />,
  CheckSent: <CheckRequest configurationSubGroup={CheckRequestConfigureSubGroup.CheckSent} checkRequestType={ECheckRequest.Check} />,
  CheckDeclined: <CheckRequest configurationSubGroup={CheckRequestConfigureSubGroup.CheckDeclined} checkRequestType={ECheckRequest.Check} />,
  CheckVoid: <CheckRequest configurationSubGroup={CheckRequestConfigureSubGroup.CheckVoid} checkRequestType={ECheckRequest.Check} />,
  ProductCheckRequest: <CheckRequest configurationSubGroup={CheckRequestConfigureSubGroup.ProductCheckRequest} checkRequestType={ECheckRequest.Product} />,
  ProductCheckRequestCut: <CheckRequest configurationSubGroup={CheckRequestConfigureSubGroup.ProductCheckRequestCut} checkRequestType={ECheckRequest.Product} />,
  ProductCheckRequestDeclined: <CheckRequest configurationSubGroup={CheckRequestConfigureSubGroup.ProductCheckRequestDeclined} checkRequestType={ECheckRequest.Product} />,
  ATRefundCheck: <CheckRequest configurationSubGroup={CheckRequestConfigureSubGroup.ATRefundCheck} checkRequestType={ECheckRequest.AtRefundCheck} />,
  Addresses: <Addresses />,
  Tracker: <Tracker />,
  Goals: <Goals />,
  OneClickEdit: <OneClickEdit />,
  ReadytoShip: <ReadytoShip />,
  ActiveShipment: <ActiveShipment />,
  FundedShipment: <FundedShipment />,
  LPQueuesMain: null,
  AccountingMain: null,
  TitlesMain: null,
  TitleFees: <TitleFees />,
};

export const productCheckRequestObj = {} as ProductCheckConfig;
export const reminderUrlByKey = {} as urlByKeyConfig;

export const getRoutes = (fetchedMenuItems: ILeftMenuItem[]) => {
  const itemsWithoutHeader = fetchedMenuItems.filter((item) => item.menuItemType !== 'Header');
  const urlByElementKey = {} as urlByKeyConfig;
  const routes = itemsWithoutHeader
    .map((item) => {
      const expLink = item.link ? item.link : '/' + item.text.replaceAll(' ', '-').toLowerCase();

      if (item.childMenuItems.length) {
        return item.childMenuItems.map((subItem) => {
          let link = String(expLink) + String(subItem.link);

          if (!subItem.link && subItem.menuItemType === 'Queue') {
            link = `${String(expLink)}/${subItem.text.replaceAll(' ', '-').toLowerCase()}`;
          }

          return {
            text: subItem.text,
            link: isExternalLink(subItem.link) ? subItem.link : link,
            menuItemType: subItem.menuItemType,
            queueType: subItem.queueType,
            queueId: subItem.queueId,
            elementKey: subItem.elementKey,
          };
        });
      }
      return {
        ...item,
        link: expLink
      };
    })
    .flat()
    .map((item) => {
      let link = item.link;
      let element = appRoutes[item.elementKey];
      let searchParams = null;
      if (!element && item.menuItemType === 'Queue') {
        element = item.queueType === 'Deal' ? <Deals title={item.text} /> : <Leads title={item.text} />;
        if (!item.link) {
          link = '/' + item.text.replaceAll(' ', '-').toLowerCase();
        }

        if (item?.queueId) {
          searchParams = withQueryParams('', { queueId: item.queueId, queueType: item.queueType }, false);
        }
      }

      if (item.elementKey) {
        if (item.elementKey === 'Reminders' || item.elementKey === 'DealStatusReminder') {
          reminderUrlByKey[item.elementKey.toString()] = {
            url: item.link,
            text: item.text,
            searchParams
          };
        }
        if (item.elementKey === 'ProductCheckRequest') {
          productCheckRequestObj.queueId = item.queueId;
          productCheckRequestObj.queueType = item.queueType;
          productCheckRequestObj.url = item.link;
          productCheckRequestObj.text = item.text;
        }
        urlByElementKey[item.elementKey.toString()] = {
          url: item.link,
          text: item.text,
          searchParams
        };
      }

      return { ...item, element: element, link };
    }) as RouteItem[];

  return { routes, urlByElementKey };
};

export const generatedRoutes = (menus: ILeftMenuItem[]) => {
  const { routes, urlByElementKey } = getRoutes(menus || []);
  const routeArr = (routes as RouteItem[]).reduce((routeArr, item) => {
    const element = ('element' in item && item.element) || <RoutePlaceholder title={item.text} />;

    if (!isExternalLink(item.link)) {
      routeArr.push({
        path: item.link,
        element: element,
      });
    }
    return routeArr;
  }, [] as reactRoutes[]);

  return { routeArr, urlByElementKey };
};

export const generateBrowserRouter = (menuItems: IMenuItem[] = []) => {
  const { routeArr, urlByElementKey } = generatedRoutes(menuItems || []);
  const routeArrWithoutDealStatusRoute = routeArr.filter((route) => route.path !== '/my-lc-queues/deal-lender-status-notification');

  const childRoutes = [
    ...routeArrWithoutDealStatusRoute,
    {
      path: '/admin-tools/add-new-contact',
      element: <CreateContact />,
    },
    {
      path: '/contact-manager',
      element: <ContactManager />,
    },
    {
      path: '/deal-sheet',
      element: <DealSheet />,
    },
    {
      path: '/search-results',
      element: <SearchResult title="Search results" />,
    },
    {
      path: '/my-lc-queues/deal-lender-status-notification',
      element: <DealStatusReminders />,
    },
    {
      path: '/stips-review-tab',
      element: <StipsReviewTab />,
    },
    {
      path: '/stips-review',
      element: <StipsReviewScreen />,
    },
    {
      path: '/vehicletitle/redirect',
      element: <RegistrationInfo />,
    },
    {
      path: '/lender-info',
      element: <LenderInfo />,
    },
    {
      path: '/assignuser-dialer',
      element: <Dialer />
    },
    {
      path: '/assignuser-livevox-dialer',
      element: <Dialer livevox/>
    },
    {
      path: '/default-role',
      element: <DefaultPage />
    },
    {
      path: '*',
      element: <UserWithoutRoles />
    },
  ];

  const router = createBrowserRouter([
    {
      path: '/',
      element: <Layout menuItems={menuItems} urlsByElementKey={urlByElementKey} />,
      children: childRoutes,
    },
  ], { basename: BASE_NAME });

  return router;
};
